import { type ChatData, chatDataKeyFactory } from '@/app/chat/_common/use-chat-data-query'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import {
  DefaultError,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query'

export function useMessageFeedbackMutation({
  onMutate,
  onError,
  onSettled,
  ...options
}: Omit<
  UseMutationOptions<
    void,
    DefaultError,
    {
      workflowId: string
      messageId: string
      feedbackType: 'POSITIVE' | 'NEGATIVE'
      feedbackContent: string
    },
    ChatData
  >,
  'mutationFn' | 'mutationKey'
> = {}) {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()
  return useMutation({
    mutationKey: ['save-message-feedback'],
    onMutate: async ({ workflowId, feedbackType, messageId, feedbackContent }, context) => {
      onMutate?.({ workflowId, feedbackType, messageId, feedbackContent }, context)
      const key = chatDataKeyFactory({ workflowId, domainId })

      await queryClient.cancelQueries({ queryKey: key })
      const previousData = queryClient.getQueryData<ChatData>(key)

      queryClient.setQueryData<ChatData>(key, (prev) => {
        return (
          prev && {
            ...prev,
            turns:
              prev.turns.map((t) => ({
                ...t,
                messages: t.messages.map((m) =>
                  m.messageId === messageId ? { ...m, feedback: feedbackType } : m,
                ),
              })) ?? [],
          }
        )
      })

      return previousData
    },
    onError: (error, variables, result, context) => {
      onError?.(error, variables, result, context)

      if (result) {
        queryClient.setQueryData<ChatData>(
          chatDataKeyFactory({ workflowId: variables.workflowId, domainId }),
          result,
        )
      }
    },
    // Always refetch after error or success:
    onSettled: (data, error, variables, result, context) => {
      onSettled?.(data, error, variables, result, context)

      if (result) {
        queryClient.invalidateQueries({
          queryKey: chatDataKeyFactory({ workflowId: variables.workflowId, domainId }),
        })
      }
    },
    mutationFn: async ({ workflowId, messageId, feedbackType, feedbackContent }) => {
      const response = await apiRequest({
        path: taskManagerApiPaths.messageFeedback({ workflowId, messageId, domainId }),
        options: {
          method: 'POST',
          body: JSON.stringify({
            feedbackType,
            feedbackContent,
          }),
        },
      })

      if (!response.ok) throw new Error('FAILED_TO_SAVE_MESSAGE_FEEDBACK')
    },
    ...options,
  })
}
